/**
 * Prompt Variable Dialog
 *
 * Modal dialog for collecting variable values from prompts
 * that contain placeholders like {{patient_name}}, {{procedure}}, etc.
 *
 * Shows a form with inputs for each variable, validates required fields,
 * and returns the filled-in prompt content.
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import type { PromptTemplate } from '@ctrl-enter/types';
import { replacePromptVariables, validatePromptVariables } from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Dialog } from './dialog';

type PromptVariableDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  prompt: PromptTemplate | null;
  onSubmit: (filledContent: string) => void;
};

export const PromptVariableDialog: React.FC<PromptVariableDialogProps> = ({
  isOpen,
  onClose,
  prompt,
  onSubmit,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when prompt changes
  useEffect(() => {
    if (prompt?.variables) {
      const initialValues: Record<string, string> = {};
      prompt.variables.forEach(variable => {
        initialValues[variable.name] = '';
      });
      setValues(initialValues);
      setErrors({});
    }
  }, [prompt]);

  const handleClose = () => {
    setValues({});
    setErrors({});
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!prompt) return;

    // Validate required variables
    const validation = validatePromptVariables(prompt, values);

    if (!validation.valid) {
      const newErrors: Record<string, string> = {};
      validation.missingVariables.forEach(varName => {
        newErrors[varName] = 'This field is required';
      });
      setErrors(newErrors);
      return;
    }

    // Replace variables in prompt content
    const filledContent = replacePromptVariables(prompt.content, values);

    // Submit the filled prompt
    onSubmit(filledContent);

    // Close dialog
    handleClose();
  };

  const handleInputChange = (varName: string, value: string) => {
    setValues(prev => ({ ...prev, [varName]: value }));

    // Clear error for this field
    if (errors[varName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[varName];
        return newErrors;
      });
    }
  };

  if (!prompt) return null;

  const hasVariables = prompt.variables && prompt.variables.length > 0;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      aria-labelledby="prompt-variable-title"
      className="max-w-lg"
    >
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h2 id="prompt-variable-title" className="text-lg font-semibold text-foreground">{prompt.title}</h2>
            {prompt.description && (
              <p className="text-sm text-muted-foreground mt-1">{prompt.description}</p>
            )}
          </div>
        </div>
        <Button onClick={handleClose} variant="ghost" size="icon" aria-label="Close">
          <X className="h-5 w-5 text-muted-foreground" />
        </Button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {hasVariables ? (
            <>
              <p className="text-sm text-muted-foreground">
                Fill in the following information to customize this prompt:
              </p>

              <div className="space-y-4">
                {prompt.variables!.map(variable => (
                  <div key={variable.name}>
                    <label
                      htmlFor={variable.name}
                      className="block text-sm font-medium text-foreground mb-1.5"
                    >
                      {variable.label}
                      {variable.required && <span className="text-destructive ml-1">*</span>}
                    </label>
                    <input
                      id={variable.name}
                      type="text"
                      value={values[variable.name] || ''}
                      onChange={e => handleInputChange(variable.name, e.target.value)}
                      placeholder={variable.placeholder}
                      className={cn(
                        'w-full px-3 py-2 text-sm',
                        'bg-background border rounded-md',
                        'placeholder:text-muted-foreground',
                        'focus:outline-none focus:ring-2 focus:ring-ring',
                        errors[variable.name] ? 'border-destructive' : 'border-border'
                      )}
                      autoFocus={variable === prompt.variables![0]}
                    />
                    {errors[variable.name] && (
                      <p className="text-xs text-destructive mt-1">{errors[variable.name]}</p>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                This prompt will be inserted into your message:
              </p>
              <div className="p-3 bg-muted rounded-md text-sm text-foreground whitespace-pre-wrap">
                {prompt.content}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            <Sparkles className="h-4 w-4" />
            <span>Use Prompt</span>
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
